// @ts-check
// @arch:pure-engine
// decideToolDispatch — strangler stage 1 of the agentLoop tool-dispatch spine.
//
// Extracts the PURE, synchronous short-circuit cluster that runs AFTER args are
// repaired and the `tool_call` event is emitted, and BEFORE the auto-review /
// approval / execute stages: the read-only deny, the toolGate deny, and the
// send_to_user delivery. These three are pure (no async, no I/O, no state
// mutation) — they only decide WHAT events and spine-events should fire. The loop
// applies them. Branch ORDER is preserved exactly: readOnly → toolGate →
// send_to_user → proceed.
//
// Equivalence with the previous inline code is proven by the deterministic goldens
// (agentLoop.spineGolden / characterization / invariants.property), which pin the
// full event sequence + final accumulator state. The async stages (approval,
// execute) stay inline — they get the flag+shadow wrapper when extracted on demand
// (code-quality sprint PR9a; see STATUS decision log).
//
// `events` and `spineEvents` are independent ordered lists: applySpineEvent never
// calls onEvent and onEvent never reads the spine state, so applying all events
// then all spine-events yields the same onEvent sequence and the same final state
// as the original interleaved calls.
//
// @param {object}  ctx
// @param {{ id:string, name:string }} ctx.tc          the tool call
// @param {any}     ctx.repairedArgs                     repaired tool arguments
// @param {boolean} ctx.readOnly                         analysis (read-only) mode
// @param {boolean} ctx.isMutating                       this tool mutates (write/exec)
// @param {{ denied:boolean, reason?:string }} ctx.toolGate  evaluateToolGate result
// @param {boolean} ctx.sendToUserOn                     apolo_send_to_user flag
// @param {boolean} [ctx.isExcluded]                     tool excluído deste run (excludeTools)
// @returns {{ action:'deny'|'sendToUser'|'proceed', events:object[], spineEvents:object[] }}
export function decideToolDispatch({ tc, repairedArgs, readOnly, isMutating, toolGate, sendToUserOn, isExcluded = false }) {
  // 0. excludeTools (RCA lentidão fix A) — tool retirada do toolset DESTE run (ex.:
  //    update_todo num worker do DAG, onde o EXECUTOR já flipa o board). O def nem é
  //    enviado ao modelo; isto é o cinto-e-suspensório pra um nome alucinado.
  if (isExcluded) {
    const denied = `Erro: a ferramenta ${tc.name} não está disponível nesta task (o harness cuida disso automaticamente). Siga com o trabalho da task.`;
    return denyResult(tc, denied);
  }

  // 1. read-only worker mode — hard-deny mutating tools at dispatch (the last gate
  //    under def-filtering; a hallucinated mutating name still cannot write/exec).
  if (readOnly && isMutating) {
    const denied = `Erro: modo análise (read-only) ativo — a ferramenta ${tc.name} (escrita) está bloqueada. Apenas leia e relate; não modifique, crie ou delete arquivos.`;
    return denyResult(tc, denied);
  }

  // 2. toolGate — policy + file-deletion/external-write guardrail deny.
  if (toolGate.denied) {
    const denied = `Erro: ${toolGate.reason}. Ação bloqueada — escolha outra abordagem.`;
    return denyResult(tc, denied);
  }

  // 3. send_to_user — deliver `message` verbatim to the user; delivery-only, no
  //    approval, no state change, continues the turn.
  if (sendToUserOn && tc.name === 'send_to_user') {
    const message = typeof repairedArgs?.message === 'string' ? repairedArgs.message : '';
    const events = [];
    const spineEvents = [];
    if (message) {
      events.push({ type: 'send_to_user', content: message });
      spineEvents.push({ type: 'set_final_text', text: message });
    }
    const ack = '{"ok":true}';
    events.push({ type: 'tool_result', id: tc.id, name: tc.name, result: ack, approved: true });
    spineEvents.push({ type: 'convo_push', message: { role: 'tool', tool_call_id: tc.id, content: ack } });
    spineEvents.push({ type: 'tool_name', id: tc.id, name: tc.name });
    spineEvents.push({ type: 'tool_executed' });
    return { action: 'sendToUser', events, spineEvents };
  }

  return { action: 'proceed', events: [], spineEvents: [] };
}

function denyResult(tc, denied) {
  return {
    action: 'deny',
    events: [{ type: 'tool_result', id: tc.id, name: tc.name, result: denied, approved: false }],
    spineEvents: [{ type: 'convo_push', message: { role: 'tool', tool_call_id: tc.id, content: denied } }],
  };
}

/**
 * RCA lentidão fix A — resolve a exclusão de tools de UM run (excludeTools): tira os
 * defs excluídos ANTES do envio (menos prompt; o modelo nem vê a tool) e devolve o
 * Set pro deny de dispatch (nome alucinado). Lista vazia ⇒ MESMO array (identidade
 * preservada, zero custo no caminho default). Extraída do spine (dp-loc ratchet).
 * @param {Array<object>} defs        tool defs prontos pro envio
 * @param {Array<string>} [excludeTools]
 * @returns {{ defs: Array<object>, excluded: Set<string> }}
 */
export function resolveToolExclusion(defs, excludeTools) {
  const list = Array.isArray(excludeTools) ? excludeTools : [];
  // Voice conversational turns pass ['*'] to wipe the whole toolset in one shot
  // (no tool-round latency; model cannot narrate/auth via tools).
  if (list.some((n) => String(n) === '*')) {
    const excluded = new Set(['*']);
    // Any hallucinated name must also be denied at dispatch (Set normally only
    // has '*'; override has so excludedToolNames.has(tc.name) is always true).
    excluded.has = () => true;
    return { defs: [], excluded };
  }
  const excluded = new Set(list.map((n) => String(n || '')).filter(Boolean));
  if (!excluded.size) return { defs, excluded };
  return { defs: defs.filter((d) => !excluded.has(d?.function?.name)), excluded };
}
